/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { bool, func, object } from 'prop-types';
import { format } from 'date-fns';

// Material UI
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import FlexOptions from '../FlexOptions/FlexOptions';

import { getIsPassportUserLoggedIn, getShopRunnerToken } from '../../../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportSubscriptionStatus } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { sanitizeContent } from '../../../../../../../../../lib/sanitize';
import { getFormattedDate } from '../../../Helpers/Helper';
import { calendarColorStyle } from '../../../Helpers/Style-Helper';

const useStyles = makeStyles(
    (theme) => ({
        arrivalDateContainer: {
            padding: '0 0 24px 48px',
            color: calendarColorStyle.primary.main,
            [theme.breakpoints.down(490)]: {
                padding: '0 48px 16px',
                borderBottom: `1px solid ${calendarColorStyle.primary[100]}`,
                flexGrow: 0,
                maxWidth: '100%',
                flexBasis: '100%',
            },
        },
        arrivalDateContainerWide: {
            padding: '0 38px 16px',
            [theme.breakpoints.down(490)]: {
                padding: '0 16px 16px',
            },
        },
        title: {
            fontSize: '1em',
            display: 'block',
            verticalAlign: 'top',
            margin: '0',
            lineHeight: '1.313em',
            [theme.breakpoints.down(490)]: {
                textAlign: 'center',
                width: '100%',
            },
        },
        subTitle: {
            display: 'block',
            fontSize: '1em',
            fontWeight: 'bold',
            margin: '0',
            width: '100%',
            lineHeight: '1.313em',
            [theme.breakpoints.down(490)]: {
                textAlign: 'center',
                width: '100%',
            },
        },
    }),
    { name: 'EnterpriseCalendarFooter' },
);

const EnterpriseCalendarFooter = ({
    calendarData,
    cmsData,
    flexOptionSelected,
    forceExactDay,
    orderItem,
    selectedDeliveryDateObj,
    selectedHoliday,
    setFlexOptionSelected,
    shipHolidayDetails,
}) => {
    const classes = useStyles();

    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const passportSubscriptionStatus = useSelector(getPassportSubscriptionStatus);
    const shopRunnerLoggedIn = useSelector(getShopRunnerToken);

    let {
        calendarDateType,
        dateObj,
        deliveryDate,
        flexDetails,
        guaranteedChargeToApply,
        holidayCode,
        rangeStartDate,
        upcharge,
        surcharge,
    } = selectedDeliveryDateObj;

    // If the delivery date is not available in the selectedDeliveryDateObj,
    // use the delivery date from the order item to build the arrival date title and subtitle
    if (!selectedDeliveryDateObj.deliveryDate && orderItem.deliveryInfo.deliveryDate) {
        calendarDateType = orderItem.deliveryInfo.deliveryDateType;
        dateObj = new Date(orderItem.deliveryInfo.deliveryDate);
        deliveryDate = orderItem.deliveryInfo.deliveryDate;
        flexDetails = orderItem.flexDetails;
        guaranteedChargeToApply = orderItem.guaranteedChargeToApply;
        holidayCode = orderItem.deliveryInfo.holidayCode;
        rangeStartDate = orderItem.deliveryInfo.deliveryRangeStartDate;
        upcharge = orderItem.itemShipping?.upCharge || 0;

        if (orderItem.itemShipping?.surCharges?.length > 0) {
            surcharge = orderItem.itemShipping.surCharges.reduce((acc, curr) => acc + parseFloat(curr.surcharge), 0);
        }
    }

    let arrivalDateTitle = '';
    let arrivalDateSubtitle = 'Standard Charges';

    const isMonthDatedCalendar = calendarData.deliveryIndicator === 'MO';
    const isWeekDatedCalendar = calendarData.deliveryIndicator === 'WO';
    const typeServiceCharge = orderItem.product.productType === 'GPT' ? 'Shipping' : 'Service';

    const totalCharges = (parseFloat(upcharge || 0, 10)) + (parseFloat(surcharge || 0, 10));

    if (
        !forceExactDay
        && selectedHoliday && holidayCode
        && shipHolidayDetails[holidayCode]?.length > 0
    ) {
        const holidayDetails = shipHolidayDetails[holidayCode][0];
        arrivalDateTitle = `Guaranteed by ${holidayDetails.holidayDesc}`;
    } else if (isWeekDatedCalendar || isMonthDatedCalendar) {
        const deliveryDateValue = rangeStartDate || deliveryDate;
        let deliveryEndDate = '';
        let deliveryDateMonth = '';
        if (deliveryDateValue) {
            deliveryEndDate = getFormattedDate(deliveryDateValue, 'MMM. do');
            deliveryDateMonth = getFormattedDate(deliveryDateValue, 'MMM. yyyy');
        }

        arrivalDateTitle = `Arrives in ${isWeekDatedCalendar ? deliveryEndDate : deliveryDateMonth}`;
    } else if (
        dateObj && calendarDateType === 'by'
    ) {
        if (typeof dateObj === 'string') {
            arrivalDateTitle = `Delivery by ${getFormattedDate(dateObj, 'MMM. do')}`;
        } else {
            arrivalDateTitle = `Delivery by ${format(dateObj, 'MMM. do')}`;
        }
    } else if (
        dateObj && (calendarDateType === 'on' || forceExactDay)
    ) {
        if (typeof dateObj === 'string') {
            arrivalDateTitle = `Delivery on ${getFormattedDate(dateObj, 'MMM. do')}`;
        } else {
            arrivalDateTitle = `Delivery on ${format(dateObj, 'MMM. do')}`;
        }
    } else if (
        calendarDateType === 'between'
    ) {
        const delStartDate = getFormattedDate(rangeStartDate, 'MMM. do');
        const delEndDate = getFormattedDate(deliveryDate, 'MMM. do');
        arrivalDateTitle = sanitizeContent(`Delivery ${delStartDate} &mdash; ${delEndDate}`);
    }

    if (forceExactDay && calendarDateType === 'between' && guaranteedChargeToApply) {
        arrivalDateSubtitle = `Standard Charges + $${guaranteedChargeToApply}`;
    } else if (totalCharges > 0) {
        arrivalDateSubtitle = `Standard Charges + $${totalCharges.toFixed(2)}`;
    }

    if (shopRunnerLoggedIn) {
        if (totalCharges > 0) {
            arrivalDateSubtitle = `$${totalCharges.toFixed(2)} ShopRunner ${typeServiceCharge}`;
        } else {
            arrivalDateSubtitle = `Free ShopRunner ${typeServiceCharge}`;
        }
    }

    if (isPassportUser || passportSubscriptionStatus) {
        if (forceExactDay && calendarDateType === 'between' && guaranteedChargeToApply) {
            arrivalDateSubtitle = `$${guaranteedChargeToApply} Passport ${typeServiceCharge}`;
        } else if (totalCharges > 0) {
            arrivalDateSubtitle = `$${totalCharges.toFixed(2)} Passport ${typeServiceCharge}`;
        } else {
            arrivalDateSubtitle = `Free Passport ${typeServiceCharge}`;
        }
    }

    return (
        <Grid
            item
            xs={isMonthDatedCalendar || isWeekDatedCalendar || selectedDeliveryDateObj?.flexDetails?.option?.length > 0 ? 12 : 7}
            className={`${classes.arrivalDateContainer} ${isMonthDatedCalendar || isWeekDatedCalendar || selectedDeliveryDateObj?.flexDetails?.option?.length > 0 ? classes.arrivalDateContainerWide : ''}`}
        >
            {
                flexDetails
                    ? (
                        <FlexOptions
                            cmsData={cmsData}
                            flexOptionSelected={flexOptionSelected}
                            flexDetails={flexDetails}
                            orderItem={orderItem}
                            selectedDeliveryDateObj={selectedDeliveryDateObj}
                            setFlexOptionSelected={setFlexOptionSelected}
                        />
                    ) : (
                        <>
                            <Grid container>
                                {
                                    arrivalDateTitle
                                        ? (
                                            <>
                                                <p id="title" className={classes.title}>{arrivalDateTitle}</p>
                                                <p id="subtitle" className={classes.subTitle}>{arrivalDateSubtitle}</p>
                                            </>
                                        ) : null
                                }
                            </Grid>
                        </>
                    )
            }
        </Grid>
    );
};

EnterpriseCalendarFooter.propTypes = {
    calendarData: object.isRequired,
    cmsData: object.isRequired,
    flexOptionSelected: object.isRequired,
    forceExactDay: bool.isRequired,
    orderItem: object.isRequired,
    selectedDeliveryDateObj: object.isRequired,
    selectedHoliday: object.isRequired,
    setFlexOptionSelected: func.isRequired,
    shipHolidayDetails: object.isRequired,
};

export default EnterpriseCalendarFooter;
